import type { Product } from '@libs/common-next';
import { Currency, getLocale, Locale } from '@libs/core';

import type { EventTracking, YesNo } from '../types';

export const dataLayerPush = (props?: EventTracking) => {
    if (props?.uniqueEventId) {
        return;
    }

    if (!window.dataLayer) {
        window.dataLayer = [];
    }

    if (props) {
        window.dataLayer.push(props);
    }
};

const DEFERRED_EVENTS_STORAGE_KEY = 'analytics_deferred_datalayer_events';

/**
 * Stores a dataLayer event in sessionStorage so it survives a full-page navigation.
 * Call flushDeferredDataLayerEvents on the destination page to replay the events.
 */
export const deferDataLayerPush = (props: EventTracking): void => {
    if (typeof window === 'undefined') return;

    try {
        const raw = sessionStorage.getItem(DEFERRED_EVENTS_STORAGE_KEY);
        const events: EventTracking[] = raw ? (JSON.parse(raw) as EventTracking[]) : [];
        events.push(props);
        sessionStorage.setItem(DEFERRED_EVENTS_STORAGE_KEY, JSON.stringify(events));
    } catch {
        dataLayerPush(props);
    }
};

/**
 * Reads any events stored by deferDataLayerPush, pushes them to the dataLayer,
 * and clears the storage. Call this once on the destination page mount.
 */
export const flushDeferredDataLayerEvents = (): void => {
    if (typeof window === 'undefined') return;

    try {
        const raw = sessionStorage.getItem(DEFERRED_EVENTS_STORAGE_KEY);
        if (!raw) return;

        const events: EventTracking[] = JSON.parse(raw) as EventTracking[];
        sessionStorage.removeItem(DEFERRED_EVENTS_STORAGE_KEY);
        events.forEach(event => dataLayerPush(event));
    } catch {
        // Ignore parse/storage errors silently
    }
};

export type ExistingSubscriptionResolver = (product: Product) => YesNo;

export type Item = {
    item_name: string;
    item_id: string;
    price?: number;
    index: number;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_brand?: string;
    item_variant?: string;
    item_list_name?: string;
    item_list_id?: string;
    item_search_term?: string | null;
    existing_subscription?: YesNo;
};

const createItems = (
    products: Product[],
    listName: string,
    listId: string,
    searchTerm: string | null = '',
    resolveExistingSubscription?: ExistingSubscriptionResolver
) => {
    const items: Item[] = [];

    products.forEach((product: Product, i: number) => {
        const existing_subscription = resolveExistingSubscription?.(product);
        items.push({
            item_name: product.title ?? '',
            item_id: product.masterProductID?.toString() ?? '',
            price: product.prices ? parseFloat(product.prices[product.prices.length - 1].toString()) : undefined,
            index: product.__position ?? i + 1,
            item_category: product.merchandisingHierarchy?.vicePresidentName ?? '',
            item_category2: product.merchandisingHierarchy?.division ?? '',
            item_category3: product.merchandisingHierarchy?.department ?? '',
            item_category4: product.merchandisingHierarchy?.class ?? '',
            item_category5: product.merchandisingHierarchy?.category ?? '',
            item_brand: product.brand,
            item_variant: `${product.id}`,
            item_list_name: listName,
            item_list_id: listId,
            item_search_term: searchTerm,
            ...(existing_subscription && { existing_subscription }),
        });
    });

    return items;
};

const resolvePageCategory = (category: string | null | undefined) => (category === undefined ? '' : category);

export const createItemsViewEvent = (
    products: Product[],
    listName: string,
    listId: string,
    filteredCategories: Array<string | null> = [],
    searchTerm: string | null = '',
    resolveExistingSubscription?: ExistingSubscriptionResolver
) => {
    const items: Item[] = createItems(products, listName, listId, searchTerm, resolveExistingSubscription);
    const locale = getLocale();
    const currency = locale === Locale['en-US'] ? Currency.USD : Currency.CAD;

    return {
        ecommerce: {
            currency,
            category: resolvePageCategory(filteredCategories[0]),
            sub_category1: resolvePageCategory(filteredCategories[1]),
            sub_category2: resolvePageCategory(filteredCategories[2]),
            items,
        },
    };
};
